import { ref, computed, watch } from 'vue'
import { doc, onSnapshot } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '../firebase'
import { useAuth } from './useAuth'
import { useAnalytics } from './useAnalytics'

const { setUserProperties } = useAnalytics()

const isPremium = ref(false)
const subscription = ref(null)
const loading = ref(true)
const error = ref(null)

let unsubscribe = null

const isDev = process.env.NODE_ENV === 'development'
const devPremiumKey = 'dev_isPremium'
const readDevPremiumOverride = () => {
  if (!isDev) return null
  try {
    const raw = localStorage.getItem(devPremiumKey)
    if (raw === 'true') return true
    if (raw === 'false') return false
  } catch (_) {
    // Ignore localStorage errors (e.g. disabled in browser settings)
  }
  return null
}

export function useSubscription() {
  const { user, isLoggedIn } = useAuth()

  const subscriptionStatus = computed(() => subscription.value?.status || null)
  const isActive = computed(() => ['active', 'trialing'].includes(subscriptionStatus.value))
  const willCancel = computed(() => subscription.value?.cancelAtPeriodEnd === true)
  const currentPeriodEnd = computed(() => subscription.value?.currentPeriodEnd?.toDate?.() || null)

  // Watch for auth changes and set up subscription listener
  watch(
    () => user.value?.uid,
    (uid) => {
      const devOverride = readDevPremiumOverride()

      // Clean up previous listener
      if (unsubscribe) {
        unsubscribe()
        unsubscribe = null
      }

      if (!uid) {
        isPremium.value = devOverride ?? false
        subscription.value = null
        loading.value = false
        return
      }

      loading.value = true

      // In dev mode, sync the premium override to Firestore emulator (bypasses security rules via admin auth)
      if (devOverride === true) {
        const projectId = 'my-karaoke-world'
        const emulatorUrl = `http://localhost:9101/v1/projects/${projectId}/databases/(default)/documents/users/${uid}?updateMask.fieldPaths=isPremium`
        fetch(emulatorUrl, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer owner'
          },
          body: JSON.stringify({
            fields: {
              isPremium: { booleanValue: true }
            }
          })
        }).catch((err) => console.warn('Dev premium sync failed:', err))
      }

      // Real-time listener for user's subscription status
      unsubscribe = onSnapshot(
        doc(db, 'users', uid),
        (doc) => {
          if (doc.exists()) {
            const data = doc.data()
            isPremium.value = devOverride ?? data.isPremium === true
            subscription.value = data.subscription || null
            setUserProperties({ isPremium: isPremium.value })
          } else {
            isPremium.value = devOverride ?? false
            subscription.value = null
            setUserProperties({ isPremium: isPremium.value })
          }
          loading.value = false
        },
        (err) => {
          console.error('Error listening to subscription:', err)
          error.value = 'Failed to load subscription status'
          loading.value = false
        }
      )
    },
    { immediate: true }
  )

  const goToPremium = async () => {
    if (!isLoggedIn.value) {
      throw new Error('Must be logged in to subscribe')
    }

    error.value = null

    try {
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession')
      const baseUrl = window.location.origin

      const result = await createCheckoutSession({
        successUrl: `${baseUrl}?subscription=success`,
        cancelUrl: `${baseUrl}?subscription=canceled`
      })

      // Redirect to Stripe Checkout
      window.location.href = result.data.url
    } catch (err) {
      console.error('Error starting checkout:', err)
      error.value = 'Failed to start checkout. Please try again.'
      throw err
    }
  }

  const manageSubscription = async () => {
    if (!isLoggedIn.value) {
      throw new Error('Must be logged in to manage subscription')
    }

    if (!isPremium.value) {
      throw new Error('No active subscription to manage')
    }

    error.value = null

    try {
      const createPortalSession = httpsCallable(functions, 'createPortalSession')

      const result = await createPortalSession({
        returnUrl: window.location.href
      })

      // Redirect to Stripe Customer Portal
      window.location.href = result.data.url
    } catch (err) {
      console.error('Error opening portal:', err)
      error.value = 'Failed to open subscription portal. Please try again.'
      throw err
    }
  }

  return {
    // State
    isPremium,
    subscription,
    loading,
    error,

    // Computed
    subscriptionStatus,
    isActive,
    willCancel,
    currentPeriodEnd,

    // Actions
    goToPremium,
    manageSubscription
  }
}
